import React, { useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../store";
import { selectors } from "../../../store/selectors/shipBuilderSelectors";
import * as derivedSelectors from "../../../store/selectors/derivedSelectors";
import { setPowerPlant } from "../../../store/features/shipBuilderSlice";
import { selectCentralizedPowerRequirements } from "../../../utils/calculations/centralizedPowerCalculations";
import { calculateJumpFuelConsumption } from "../../../utils/calculations/fuelCalculations";

/**
 * Component to display ship warnings and validation issues
 * Shows warnings for weight, power, jump/thrust, and fuel problems
 */
export const ShipWarnings: React.FC = () => {
  const dispatch = useAppDispatch();
  const shipStats = useAppSelector(selectors.selectShipStats);
  const shipSize = useAppSelector(selectors.selectShipSize);
  const drives = useAppSelector(selectors.selectDrives);
  const powerPlant = useAppSelector(selectors.selectPowerPlant);
  
  // Use centralized power calculations for consistency
  const powerRequirements = useAppSelector(selectCentralizedPowerRequirements);

  const fuel = useAppSelector(selectors.selectFuel);
  const fuelTanks = fuel?.weight || 0;

  const weightOverrideActive = useAppSelector(selectors.selectOverrideWeight);
  const powerOverrideActive = useAppSelector(selectors.selectPowerOverrideActive);

  // Use the same selectors as Step 11 to get total accommodation capacity
  const totalCapacity = useAppSelector(derivedSelectors.selectTotalStateroomCapacity);

  // Force recalculation when component mounts to ensure power requirements are updated
  useEffect(() => {
    console.log("ShipWarnings: useEffect triggered, powerPlant:", powerPlant);
    if (powerPlant) {
      // Dispatch the same power plant to trigger recalculation
      console.log("ShipWarnings: Dispatching setPowerPlant to trigger recalculation");
      dispatch(setPowerPlant(powerPlant));
    }
  }, [dispatch, powerPlant]);

  console.log("ShipWarnings: Current shipStats:", shipStats);
  console.log("ShipWarnings: powerRequired:", shipStats.powerRequired);
  console.log("ShipWarnings: totalPower:", shipStats.totalPower);

  const warnings: string[] = [];
  const errors: string[] = [];

  // Weight warnings
  if (!weightOverrideActive) {
    if (shipStats.totalWeight > shipSize) {
      errors.push(
        `Ship is overweight: ${shipStats.totalWeight.toFixed(1)} tons exceeds ${shipSize} ton capacity`,
      );
    } else if (shipStats.totalWeight > shipSize * 0.95) {
      warnings.push(
        `Ship is near weight limit: ${shipStats.totalWeight.toFixed(1)}/${shipSize} tons (${((shipStats.totalWeight / shipSize) * 100).toFixed(1)}%)`,
      );
    }
  }

  // Power warnings - use centralized power calculations
  const powerRequired = powerRequirements.maximumLoad;
  const powerAvailable = powerPlant?.power || 0;
  
  // Check if basic systems + maneuver drive can operate simultaneously (good practice)
  const basicAndManeuverPower = powerRequirements.normalOperations;
  if (!powerOverrideActive && basicAndManeuverPower > powerAvailable) {
    errors.push(
      `Insufficient power for basic operations: ${basicAndManeuverPower} Power required for basic systems + maneuver drive, ${powerAvailable} Power available`,
    );
  } else if (!powerOverrideActive && powerRequired > powerAvailable) {
    // Having insufficient power for all systems simultaneously is not critical - it's just a design consideration
    warnings.push(
      `Limited power capacity: ${powerRequired} Power required for all systems, ${powerAvailable} Power available. Consider powering down some systems during jump operations.`,
    );
  } else if (!powerOverrideActive && powerRequired > powerAvailable * 0.9) {
    warnings.push(
      `Low power margin: ${powerRequired}/${powerAvailable} Power (${((powerRequired / powerAvailable) * 100).toFixed(1)}%)`,
    );
  }

  // Jump drive warnings
  const jumpDrive = drives.find((d) => d.type === "jump");
  if (jumpDrive) {
    const jumpFuelRequired = calculateJumpFuelConsumption(jumpDrive.rating, shipSize);
    if (fuelTanks < jumpFuelRequired) {
      errors.push(
        `Insufficient fuel for Jump-${jumpDrive.rating}: ${fuelTanks} tons available, ${jumpFuelRequired.toFixed(0)} tons required`,
      );
    }
    // Removed low fuel margin warning - not an issue in Traveller system
  }

  // Reaction drive warnings (maneuver drives don't require fuel)
  const reactionDrive = drives.find((d) => d.type === "reaction");
  if (reactionDrive) {
    // Reaction drives require 2.5% of ship tonnage per thrust per hour
    // For 1 week at full thrust: 2.5% × ship tonnage × thrust × 168 hours
    const reactionFuelRequired = shipSize * 0.025 * reactionDrive.rating * 168;
    if (fuelTanks < reactionFuelRequired) {
      warnings.push(
        `Limited reaction drive fuel: ${fuelTanks} tons available, ${reactionFuelRequired.toFixed(0)} tons for 1 week at full thrust`,
      );
    }
  }

  // Crew warnings
  const assignedCrewCount = useAppSelector(derivedSelectors.selectAssignedCrewCount);
  
  if (assignedCrewCount > 0) {
    if (assignedCrewCount > totalCapacity) {
      warnings.push(
        `Crew exceeds accommodations: ${assignedCrewCount} crew, ${totalCapacity} total accommodation capacity`,
      );
    }
  }

  // Cargo warnings
  const availableCargo = shipSize - shipStats.totalWeight;
  if (availableCargo < 0) {
    errors.push(
      `No cargo space available: ${Math.abs(availableCargo).toFixed(1)} tons overweight`,
    );
  } else if (availableCargo < shipSize * 0.1) {
    warnings.push(
      `Limited cargo space: ${availableCargo.toFixed(1)} tons available (${((availableCargo / shipSize) * 100).toFixed(1)}% of ship)`,
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Ship Validation & Warnings
      </h3>

      {/* Errors Section */}
      {errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <h4 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-3 flex items-center">
            <span className="mr-2">🚨</span>
            Critical Issues ({errors.length})
          </h4>
          <ul className="space-y-2">
            {errors.map((error, index) => (
              <li key={index} className="text-red-700 dark:text-red-300 flex items-start">
                <span className="mr-2">•</span>
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Warnings Section */}
      {warnings.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
          <h4 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-3 flex items-center">
            <span className="mr-2">⚠️</span>
            Warnings ({warnings.length})
          </h4>
          <ul className="space-y-2">
            {warnings.map((warning, index) => (
              <li key={index} className="text-yellow-700 dark:text-yellow-300 flex items-start">
                <span className="mr-2">•</span>
                {warning}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Success Message */}
      {errors.length === 0 && warnings.length === 0 && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4">
          <h4 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-3 flex items-center">
            <span className="mr-2">✅</span>
            Ship Design Valid
          </h4>
          <p className="text-green-700 dark:text-green-300">
            Your ship design meets all basic requirements and has no critical
            issues.
          </p>
        </div>
      )}

      {/* Ship Statistics Summary */}
      <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">
          Ship Statistics
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-600 dark:text-gray-300">Total Weight:</span>
            <div className="text-gray-900 dark:text-gray-100">
              {shipStats.totalWeight?.toFixed(1) || 0} tons
            </div>
          </div>
          <div>
            <span className="font-medium text-gray-600 dark:text-gray-300">Power Required:</span>
            <div className="text-gray-900 dark:text-gray-100">
              {powerRequired || 0} Power
            </div>
          </div>
          <div>
            <span className="font-medium text-gray-600 dark:text-gray-300">Power Available:</span>
            <div className="text-gray-900 dark:text-gray-100">
              {powerAvailable || 0} Power
            </div>
          </div>
          <div>
            <span className="font-medium text-gray-600 dark:text-gray-300">Fuel Tanks:</span>
            <div className="text-gray-900 dark:text-gray-100">{fuelTanks || 0} tons</div>
          </div>
        </div>
      </div>
    </div>
  );
};
